import Highcharts from 'highcharts';
import debounce from 'lodash/debounce';
import sortBy from 'lodash/sortBy';
import {AttrsSchema, Component} from 'panel';
import HighchartsAnnotations from 'highcharts/modules/annotations';
import ResizeObserver from 'resize-observer-polyfill';

import {getTooltipPosition, patchHighchartsForNegativeLog} from 'common/report/util/highcharts';
import {abbreviateNumber, htmlEncodeString} from 'common/util';
import {isIncompleteInterval} from 'common/util/chart';
import * as colors from 'common/util/colors';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {DEFAULT_CHART_HEIGHT, DEFAULT_CHART_WIDTH} from 'common/report/constants';
import mpFonts from 'common/stylesheets/mixins/fonts.json';
import {listenForPropUpdate} from 'common/util/component-helpers';
import {formatDate, MS_BY_UNIT, UNITS} from 'common/util/date';

import {DisplayAnalysisType, DisplayPlotStyle, DisplayStackingType, LineStyle, XAxisUnit} from './enums';
import {IChartDataItem, LineChartState, Popover, XAxisOptions, YAxisOptions} from './types';
import {template} from './template';
import css from './index.styl?inline';
import {Annotations} from './annotations';

patchHighchartsForNegativeLog(Highcharts);
HighchartsAnnotations(Highcharts);

function getColorWithAlpha(color: string, alpha: number /* float in range [0, 1] */): string {
  return new Highcharts.Color(color).setOpacity(alpha).get(`rgba`);
}

function getAlphaFromColor(color: string): number /* returns float in range [0, 1] */ {
  const rgbaString = new Highcharts.Color(color).get(`rgba`);
  const [, , , alpha] = rgbaString.replace(/^rgba?\(|\s+|\)$/g, ``).split(`,`);
  return parseFloat(alpha);
}

export const LINE_CHART_PROP_KEYS = [
  `chartData`,
  `formatPointX`,
  `formatPointY`,
  `formatTicksX`,
  `formatTooltip`,
  `formatXAxisLabel`,
  `formatYAxisLabel`,
  `onHighChartClick`,
  `popover`,
  `onHighchartMouseOut`,
  `onHighchartMouseOver`,
] as const;

type ArrayType<T> = T extends ReadonlyArray<infer U> ? U : never;

export type LineChartProps = Pick<LineChart, ArrayType<typeof LINE_CHART_PROP_KEYS>>;

export interface LineChartAttrs {
  'chart-height': number;
  'chart-width': number;
  'chart-margin-left': number;
  'chart-margin-right': number;
  'color-theme': string;
  'display-analysis-type': DisplayAnalysisType;
  'display-plot-style': DisplayPlotStyle;
  'display-stacking-type': DisplayStackingType;
  'download-mode': boolean;
  'incomplete-to-date': boolean;
  'incomplete-from-date': boolean;
  'hide-y-axis-ticks': boolean;
  'show-annotations': boolean;
  'show-popover': boolean;
  'time-unit': TimeUnit;
  timezone: string;
  'utc-offset': number;
  'x-axis-options': XAxisOptions;
  'y-axis-label-alignment': string;
  'y-axis-label-horizontal-offset': number;
  'y-axis-label-vertical-offset': number;
  'y-axis-options': YAxisOptions;
  'zoom-type': string;
}

export class LineChart extends Component<LineChartState, LineChartAttrs> {
  public chartData: IChartDataItem[];
  public formatPointX?: (x: number) => string;
  public formatPointY?: (y: number) => string;
  public formatTicksX?: (min: number, max: number) => number[];
  public formatYAxisLabel?: (value: number) => string;
  public formatXAxisLabel?: (value: number) => string;
  public onHighchartMouseOut?: (highchartChart: any) => void;
  public onHighchartMouseOver?: (
    highchartChart: any,
    seriesIdx: number,
    dataIdx: number,
    color: string,
    plotX: number,
    plotY: number,
  ) => void;
  public onHighChartClick?: (
    highchartChart: any,
    seriesIdx: number,
    dataIdx: number,
    color: string,
    plotX: number,
    plotY: number,
  ) => void;
  // formatTooltip's @tooltipContext argument type is documented here: https://api.highcharts.com/class-reference/Highcharts.TooltipFormatterContextObject
  // formatTooltip's @series argument is the same as tooltipContext.series
  public formatTooltip?: (x: number, y: number, series: any, idx: number, tooltipContext: any) => string;
  public popover?: () => Popover;
  public annotations: Annotations;
  public selectedSeriesPoint: [seriesIndex: number, pointIndex: number];

  public static get attrsSchema(): AttrsSchema<LineChartAttrs> {
    return {
      ...super.attrsSchema,
      'chart-height': {
        type: `number`,
        default: DEFAULT_CHART_HEIGHT,
        description: `height of the chart in px (set to 0 to fill container)`,
      },
      'chart-width': {
        type: `number`,
        default: DEFAULT_CHART_WIDTH,
        description: `width of the chart in px (set to 0 to fill container)`,
      },
      'chart-margin-left': {
        type: `number`,
        default: 50,
        description: `left margin of the chart in px`,
      },
      'chart-margin-right': {
        type: `number`,
        default: 10,
        description: `right margin of the chart in px`,
      },
      'color-theme': {
        type: `string`,
        default: `default`,
        description: `color theme of the lines plotted on the chart`,
      },
      'display-analysis-type': {
        type: `string`,
        default: DisplayAnalysisType.Linear,
        enum: [DisplayAnalysisType.Linear, DisplayAnalysisType.Logarithmic],
      },
      'display-plot-style': {
        type: `string`,
        default: DisplayPlotStyle.Standard,
        enum: [DisplayPlotStyle.Stacked, DisplayPlotStyle.Standard],
        description: `plot style, standard vs stacked`,
      },
      'display-stacking-type': {
        type: `string`,
        default: DisplayStackingType.Normal,
        enum: [DisplayStackingType.Normal, DisplayStackingType.Percent],
        description: `stacking type, only applicable for "stacked" plot style`,
      },
      'download-mode': `boolean`,
      'incomplete-to-date': {
        type: `boolean`,
        description: `zone with to date, overrides incomplete interval check, utc offset from project, which is only necessary if you want incomplete interval zoning`,
      },
      'incomplete-from-date': {
        type: `boolean`,
        description: `zone with from date as well, utc offset from project, which is only necessary if you want incomplete interval zoning`,
      },
      'hide-y-axis-ticks': {
        type: `boolean`,
        description: `hide y axis values to the right of the chart`,
      },
      'show-annotations': {
        type: `boolean`,
        description: `shows global annotations on mp-line-chart`,
      },
      'show-popover': {
        type: `boolean`,
        description: `shows action popover on mp-line-chart`,
      },
      'time-unit': {
        type: `string`,
        default: null,
        enum: [TimeUnit.Minute, TimeUnit.Hour, TimeUnit.Day, TimeUnit.Week, TimeUnit.Month, TimeUnit.Quarter],
        description: `time unit to align x axis, which is only necessary if you want incomplete interval zoning`,
      },
      timezone: `string`,
      'utc-offset': `number`,
      'x-axis-options': {
        type: `json`,
        description: `x-axis overrides for highcharts of type XAxisOptions`,
      },
      'y-axis-label-alignment': {
        type: `string`,
        description: `adds an alignment for the y axis labels, such as left or right aligned`,
      },
      'y-axis-label-horizontal-offset': {
        type: `number`,
        description: `adds a horizontal position offset to the y axis labels`,
      },
      'y-axis-label-vertical-offset': {
        type: `number`,
        description: `adds a vertical position offset to the y axis labels`,
      },
      'y-axis-options': {
        type: `json`,
        default: null,
        description: `y-axis overrides`,
        tsType: `YAxisOption`,
      },
      'zoom-type': {
        type: `string`,
        default: ``,
        enum: [``, `x`],
      },
    };
  }

  constructor() {
    super();

    // debounce in order to only run once per prop-batch update
    const updateChart = debounce(this.updateChart.bind(this), 20, {trailing: true});

    for (const prop of LINE_CHART_PROP_KEYS) {
      listenForPropUpdate(this, prop, updateChart);
    }
  }

  private highcharts: any;
  private hoveredTimestampMs: any;
  private selfResizeObserver: ResizeObserver;
  private chartHeight: number; // px

  public get config() {
    return {
      css,
      useShadowDom: true,
      template,
      helpers: {
        getChartData: () => this.chartData,
        getChartElement: () => this.el,
        getHighCharts: () => this.highcharts,
        annotations: Annotations.getHelpers(this),
        popover: {
          props: {
            close: () => this.closePopover(),
          },
          attrs: () => {
            return {
              open: this.state.popover.isActive,
            };
          },
          style: () => {
            if (!this.state.popover.selected) {
              return null;
            }

            const {plotX, plotY, highchartChart} = this.state.popover.selected;
            const popover = this.popover();
            const left = plotX - (popover.leftOffset || 0);
            const top = plotY - (popover.topOffset || 0);
            const leftAlignment =
              left + (popover.maxWidth || 0) > highchartChart.chart.clipBox.width
                ? highchartChart.chart.clipBox.width - (popover.maxWidth / 2 || 0)
                : left;
            const topAlignment = top > highchartChart.chart.clipBox.height ? highchartChart.chart.clipBox.height : top;

            return {
              // align to the click position horizontally
              left: `${leftAlignment}px`,
              // align to beneath the bar vertically
              top: `${topAlignment}px`,
            };
          },
        },
      },
    };
  }

  public connectedCallback() {
    super.connectedCallback();

    if (!this.chartData) {
      throw new Error(`"chartData" is a required component prop`);
    }

    this.selfResizeObserver = new ResizeObserver((entries) => {
      const {height} = entries[0].contentRect;
      this.chartHeight = height;
      // update the entire chart instead of only reflow(), because some chart options may be based on chartHeight
      this.updateChart();
    });
    this.selfResizeObserver.observe(this);

    const chartEl = document.createElement(`div`);
    chartEl.style.width = `100%`;
    chartEl.style.height = `100%`;
    this.el.appendChild(chartEl);

    this.highcharts = new Highcharts.Chart(chartEl, this.getChartOptions());
    // note: this.highcharts.container may not exist in isotest environment
    this.highcharts.container?.addEventListener(`mousemove`, this.handleHighchartsContainerMouseMove);
    this.highcharts.container?.addEventListener(`mouseleave`, this.handleHighchartsContainerMouseLeave);
    this.updateChart();

    if (this.attr(`show-annotations`)) {
      this.annotations = new Annotations(this);
      this.update({annotations: this.annotations.getDefaultState()});
    }
  }

  public disconnectedCallback() {
    this.selfResizeObserver.disconnect();

    if (this.annotations) {
      this.annotations.removeElements();
    }

    if (this.highcharts) {
      this.highcharts.container?.removeEventListener(`mousemove`, this.handleHighchartsContainerMouseMove);
      this.highcharts.container?.removeEventListener(`mouseleave`, this.handleHighchartsContainerMouseLeave);
      this.highcharts.destroy();
      delete this.highcharts;
    }

    super.disconnectedCallback();
  }

  getChartData() {
    return this.chartData;
  }

  getHighcharts() {
    return this.highcharts;
  }

  getHoveredTimestampMs() {
    return this.hoveredTimestampMs;
  }

  closePopover() {
    this.update({popover: {isActive: false, selected: null}});
  }

  defaultXAxisLabelFormatter(value: number, unit: TimeUnit) {
    return formatDate(value, {
      unit,
      displayRangeIfWeek: false,
      customFormatting: {
        [UNITS.minute]: `h:mma`,
        [UNITS.hour]: `MMM D, ha`,
        [UNITS.day]: `MMM D`,
        [UNITS.week]: `MMM D`,
        [UNITS.month]: `MMMM`,
        [UNITS.quarter]: `[Q]Q YYYY`,
        [UNITS.year]: `YYYY`,
      },
    });
  }

  private updateChart() {
    if (this.chartData.length && !this.state.annotations?.allAnnotations.size && !this.annotations?.areLoaded()) {
      this.annotations?.load();
    }

    if (this.highcharts) {
      this.highcharts.update(
        this.getChartOptions(),
        true, // redraw after update
        true, // oneToOne: add/remove series when they change
      );
      this.highcharts.reflow();
    }
  }

  private formatSeries() {
    // prettier-ignore
    return this.chartData
      .map((dataSegment, segmentIdx) => {
        const isStacked = this.attr(`display-plot-style`) === DisplayPlotStyle.Stacked;
        const colorIdx = segmentIdx % this.chartData.length;
        const seriesColor = dataSegment.color || colors.getChartSegmentColorCombo(colorIdx, this.attr(`color-theme`)).primary;
        const isSinglePoint = dataSegment.data.length === 1;
        return {
          name: dataSegment.name,
          label: dataSegment.label,
          data: dataSegment.data,
          type: isStacked ? `area` : `line`,
          color: seriesColor,
          ...(isStacked ? {fillColor: getColorWithAlpha(seriesColor, 0.7)} : {}),
          marker: {
            enabled: isSinglePoint,
          },
          visible: dataSegment.visible,
          dashStyle: dataSegment.dashStyle || LineStyle.Standard,
          incompleteLength: dataSegment.incompleteLength,
          isComparisonItem: dataSegment.isComparisonItem,
        };
      })
      // TODO opslead
      // eslint-disable-next-line sonarjs/cognitive-complexity
      .map((lineSeries) => {
        lineSeries.data = sortBy(lineSeries.data, (d) => d[0]);
        const timeUnit = this.attr(`time-unit`);
        let incompletePathsAndZoning = {};
        const dataLength = lineSeries.data.length;
        const showIncompleteToDate = this.attr(`incomplete-to-date`);
        const showIncompleteFromDate = this.attr(`incomplete-from-date`);
        const dashStyle = lineSeries.dashStyle;
        const unspecifiedIncompleteLength = lineSeries.incompleteLength === undefined;
        const incompleteLength =
          unspecifiedIncompleteLength || lineSeries.incompleteLength > dataLength - 1
            ? 1
            : lineSeries.incompleteLength;
        const incompleteZoneOptions = { // for Highcharts configuration
          dashStyle: LineStyle.Incomplete,
          fillColor: getColorWithAlpha(lineSeries.color, 0.5),
        };
        if (timeUnit && unspecifiedIncompleteLength) {
          const utcOffset = this.attr(`utc-offset`);
          const isIncompletePath = isIncompleteInterval(lineSeries.data, {
            unit: timeUnit,
            utcOffset,
          });
          const showIncomplete = isIncompletePath && lineSeries.data.length >= 2;
          incompletePathsAndZoning = {
            isIncompletePath,
            ...(showIncomplete && {
              zoneAxis: `x`,
              zones: [
                {value: lineSeries.data[dataLength - 2][0]},
                incompleteZoneOptions,
              ],
            }),
          };
        } else if (dataLength > 1 && incompleteLength > 0 && (showIncompleteFromDate || showIncompleteToDate)) {
          const zones = [];
          if (showIncompleteFromDate && showIncompleteToDate) {
            // Highchart has a bug with [dot, solid, dot]. So when we are changing the line style more then once,
            // we need to make sure we define both the starting and ending position for the solid part of the line.
            zones.push({value: lineSeries.data[incompleteLength][0], ...incompleteZoneOptions});
            if (incompleteLength + 1 < dataLength - incompleteLength - 1) {
              zones.push({value: lineSeries.data[incompleteLength + 1][0], dashStyle});
              zones.push({value: lineSeries.data[dataLength - incompleteLength - 1][0], dashStyle});
            } else if (incompleteLength + 1 === dataLength - incompleteLength - 1) {
              zones.push({value: lineSeries.data[incompleteLength + 1][0], dashStyle});
            }
            zones.push({value: lineSeries.data[dataLength - 1][0], ...incompleteZoneOptions});
          } else if (showIncompleteFromDate) {
            zones.push({value: lineSeries.data[incompleteLength][0], ...incompleteZoneOptions});
            if (incompleteLength < dataLength - 1) {
              zones.push({value: lineSeries.data[incompleteLength + 1][0], dashStyle});
            }
          } else {
            // Only showIncompleteToDate is true
            if (incompleteLength < dataLength - 1) {
              zones.push({value: lineSeries.data[dataLength - incompleteLength - 1][0], dashStyle});
            }
            zones.push({value: lineSeries.data[dataLength - 1][0], ...incompleteZoneOptions});
          }
          incompletePathsAndZoning = {
            isIncompletePath: true,
            zoneAxis: `x`,
            zones,
          };
        }
        return {
          ...lineSeries,
          ...incompletePathsAndZoning,
          formatPointX: this.formatPointX,
          formatPointY: this.formatPointY,
          formatTicksX: this.formatTicksX,
          onHighchartMouseOut: this.onHighchartMouseOut,
          onHighchartMouseOver: this.onHighchartMouseOver,
          onHighChartClick: this.onHighChartClick,
          popover: this.popover,
          formatTooltip: this.formatTooltip,
        };
      });
  }

  // TODO: Highcharts 7 is typed in beta, but we are using Highcharts 6. Use Highcharts 7's typing system
  // when we eventually switch over
  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private getChartOptions(): any {
    const highchartChart = this.highcharts;
    const series = this.formatSeries();
    const xAxisOptions: XAxisOptions = this.attr(`x-axis-options`);
    const yAxisOptions: YAxisOptions = this.attr(`y-axis-options`);
    const timeUnit = this.attr(`time-unit`);
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const mpLineChartComponent = this;

    return {
      chart: {
        backgroundColor: null,
        // "reflow" is normally true by default, but it only triggers on window resize events,
        // so we disable it and call reflow() manually with a ResizeObserver on this element.
        reflow: false,
        marginLeft: this.attr(`chart-margin-left`),
        marginRight: this.attr(`chart-margin-right`),
        style: {
          fontFamily: mpFonts.fontFamilyPrimary,
          fontSize: `12px,`,
        },
        spacingBottom: (() => {
          if (this.attr(`show-annotations`)) {
            return 32;
          } else if (this.chartHeight > 300) {
            return 15;
          } else {
            return 8;
          }
        })(),
        type: this.attr(`display-plot-style`) === DisplayPlotStyle.Stacked ? `area` : `line`,
        zoomType: this.attr(`zoom-type`),
        events: {
          redraw(this: TODO) {
            if (mpLineChartComponent.annotations && this.chartData?.length) {
              mpLineChartComponent.annotations.setAnnotations(
                this.xAxis[0].tickPositions[0],
                this.xAxis[0].tickPositions[this.xAxis[0].tickPositions.length - 1],
              );
            }

            if (mpLineChartComponent.selectedSeriesPoint) {
              this.series[mpLineChartComponent.selectedSeriesPoint[0]].points[
                mpLineChartComponent.selectedSeriesPoint[1]
              ].select();
            }
          },
        },
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      loading: {
        labelStyle: {
          color: `transparent`,
        },
      },
      plotOptions: {
        line: {
          cursor: `pointer`,
          lineWidth: 2,
          marker: {
            symbol: `circle`,
            lineColor: `#fff`,
            lineWidth: 2,
            radius: 5,
          },
          shadow: false,
          states: {
            hover: {
              enabled: true,
            },
          },
        },
        area: {
          lineWidth: 0,
          marker: {
            symbol: `circle`,
            lineColor: `#fff`,
            lineWidth: 2,
            radius: 5,
          },
          shadow: false,
          states: {
            hover: {
              enabled: true,
              lineWidthPlus: 0,
            },
          },
          trackByArea: true,
          stickyTracking: false,
        },
        series: {
          animation: false,
          states: {
            select: {
              halo: {
                size: 10,
                opacity: 0.25,
              },
            },
          },
          marker: {
            enabled: false,
            hover: {
              enabled: true,
            },
            lineWidth: 2,
            symbol: `circle`,
            states: {
              select: {
                lineColor: `#fff`,
                fillColor: null,
                lineWidth: 3,
                radius: 7,
              },
            },
          },
          point: {
            events: {
              mouseOver(this: TODO) {
                mpLineChartComponent.updateHoveredTimestampMs(this.x);
                const {color, index: dataIdx, plotX, plotY, series: chartSeries} = this;
                const {onHighchartMouseOver, index: seriesIdx} = chartSeries.userOptions;
                return onHighchartMouseOver
                  ? onHighchartMouseOver(highchartChart, seriesIdx, dataIdx, color, plotX, plotY)
                  : undefined;
              },
              click(_ev: MouseEvent) {
                const point = this as any;
                const {color, index: dataIdx, plotX, plotY, series: chartSeries} = point;
                const {onHighChartClick} = chartSeries.userOptions;

                mpLineChartComponent.update({
                  popover: {
                    isActive: true,
                    selected: {
                      highchartChart: chartSeries,
                      dataIdx,
                      color,
                      plotX,
                      plotY,
                    },
                  },
                });

                return onHighChartClick ? onHighChartClick(chartSeries, dataIdx, color, plotX, plotY) : undefined;
              },
            },
          },
          events: {
            mouseOut(this: TODO) {
              // series hover effect for stacked line chart (restore original options that were overwritten on mouseOver)
              if (this.type === `area` && this.userOptions.oldNonHoverOptions) {
                this.update({
                  // note: "this" is the Highcharts series, not the component
                  fillColor: this.userOptions.oldNonHoverOptions.fillColor,
                  zones: this.userOptions.oldNonHoverOptions.zones,
                  oldNonHoverOptions: undefined,
                });
              }

              // user defined callbacks
              const {onHighchartMouseOut} = this.userOptions;
              return onHighchartMouseOut ? onHighchartMouseOut(highchartChart) : undefined;
            },
            mouseOver(this: TODO) {
              // series hover effect for stacked line chart (overwrite series options and save the original options)
              if (this.type === `area` && !this.userOptions.oldNonHoverOptions) {
                const originalAlpha = getAlphaFromColor(this.options.fillColor);
                const hoverAlpha = Math.min(1, originalAlpha + 0.3);
                this.update({
                  // note: "this" is the Highcharts series, not the component
                  fillColor: getColorWithAlpha(this.options.fillColor, hoverAlpha),
                  zones: this.options.zones?.map((zone) => {
                    if (zone.fillColor) {
                      const zoneOriginalAlpha = getAlphaFromColor(zone.fillColor);
                      const zoneHoverAlpha = Math.min(1, zoneOriginalAlpha + 0.3);
                      return {...zone, fillColor: getColorWithAlpha(zone.fillColor, zoneHoverAlpha)};
                    }
                    return zone;
                  }),

                  // save original options into "oldNonHoverOptions"
                  // Highcharts automatically puts non-standard options into "userOptions"
                  oldNonHoverOptions: {
                    fillColor: this.options.fillColor,
                    zones: this.options.zones,
                  },
                });
              }
            },
          },
          shadow: false,
          ...(this.attr(`display-plot-style`) === DisplayPlotStyle.Stacked && {
            stacking: this.attr(`display-stacking-type`),
          }),
        },
      },
      series,
      time: {
        useUTC: false,
      },
      tooltip: {
        backgroundColor: null,
        borderWidth: 0,
        crosshairs: true,
        formatter(this: TODO) {
          // Highcharts callbacks don't work with arrow functions
          const {color, index, name, options} = this.series;

          // Override style to make the tooltip not look like the default
          if (options.formatTooltip) {
            return options.formatTooltip(this.x, this.y, this.series, index, this);
          }

          const formattedLeftLabel = options.formatPointX
            ? options.formatPointX(this.x)
            : Highcharts.dateFormat(`%b %e, %Y`, this.x);
          const formattedValue = options.formatPointY ? options.formatPointY(this.y) : this.y.toLocaleString();
          const seriesLabel = name;

          const defaultLabelFormatter = `
            <div class="row">
              <span class="value" style="font-weight: 700">${htmlEncodeString(formattedLeftLabel)}:</span>
              <span class="value">${formattedValue}</span>
            </div>
          `;
          return `
            <div class="line-chart-tooltip">
              <div class="title" style="background-color: ${color};">${seriesLabel}</div>
              <div class="result">
                ${defaultLabelFormatter}
              </div>
            </div>
          `;
        },
        hideDelay: 0,
        padding: 0,
        positioner(this: TODO, tooltipWidth, tooltipHeight, point) {
          const chart = this.chart;
          return getTooltipPosition({
            tooltipWidth,
            tooltipHeight,
            pointX: point.plotX,
            pointY: point.plotY,
            chartLeft: chart.plotLeft,
            chartTop: chart.plotTop,
            chartWidth: chart.plotWidth,
            chartHeight: chart.plotHeight,
          });
        },
        shadow: false,
        shape: `square`,
        useHTML: true,
      },
      title: {
        text: null,
      },
      xAxis: {
        endOnTick: false,
        formatTicksX: this.formatTicksX,
        ...(this.formatXAxisLabel ? {formatXAxisLabel: this.formatXAxisLabel} : {}),
        labels: {
          enabled: !xAxisOptions?.labels?.hideTicks,
          staggerLines: xAxisOptions?.labels?.staggerLines,
          step: xAxisOptions?.labels?.step,
          ...(xAxisOptions?.labels?.style ? {style: xAxisOptions?.labels?.style} : {style: null}),
          ...(this.formatXAxisLabel
            ? {
                formatter(this: TODO) {
                  const {userOptions} = this.axis;
                  return userOptions.formatXAxisLabel(this.value);
                },
              }
            : {
                formatter(this: TODO) {
                  if (xAxisOptions?.unit === XAxisUnit.Number) {
                    return this.value;
                  }
                  return mpLineChartComponent.defaultXAxisLabelFormatter(this.value, timeUnit);
                },
              }),
        },
        crosshair: xAxisOptions?.crosshair,
        lineWidth: 1,
        marginBottom: 10,
        maxPadding: 0,
        minPadding: 0,
        minTickInterval: MS_BY_UNIT[this.attr(`time-unit`)],
        plotBands: xAxisOptions?.plotBands,
        plotLines: xAxisOptions?.plotLines,
        tickPosition: `outside`,
        tickPositions: xAxisOptions?.tickPositions,
        tickPositioner(this: TODO) {
          const {dataMax, dataMin, options} = this;
          const {formatTicksX} = options;
          return formatTicksX ? formatTicksX(dataMin, dataMax) : undefined;
        },
        tickLength: 6,
        tickmarkPlacement: `on`,
        title: {
          text: null,
        },
        type: xAxisOptions?.unit === XAxisUnit.Number ? `linear` : `datetime`,
      },
      yAxis: {
        allowDecimals: true,
        endOnTick: true,
        gridLineColor: null,
        gridLineDashStyle: `Solid`,
        formatYAxisLabel: this.formatYAxisLabel,
        labels: {
          enabled: !this.attr(`hide-y-axis-ticks`),
          ...(this.attr(`y-axis-label-alignment`) ? {align: this.attr(`y-axis-label-alignment`)} : {}),
          ...(this.attr(`y-axis-label-horizontal-offset`) ? {x: this.attr(`y-axis-label-horizontal-offset`)} : {}),
          ...(this.attr(`y-axis-label-vertical-offset`) ? {y: this.attr(`y-axis-label-vertical-offset`)} : {}),
          formatter(this: TODO) {
            const {userOptions} = this.axis;
            return userOptions.formatYAxisLabel
              ? userOptions.formatYAxisLabel(this.value)
              : abbreviateNumber(this.value);
          },
          style: null,
        },
        // maxPadding is relative to axis length which we approximate with chartHeight
        // to produce an absolute max padding of ~5px (but slightly less because chartHeight != y-axis length)
        maxPadding: yAxisOptions?.maxPadding ?? 5 / (this.chartHeight || 100),
        minPadding: 0,
        lineWidth: 0,
        softMin: yAxisOptions?.softMin ?? undefined,
        softMax: yAxisOptions?.softMax ?? undefined,
        startOnTick: false,
        title: {
          text: null,
        },
        type:
          this.attr(`display-analysis-type`) === DisplayAnalysisType.Logarithmic
            ? DisplayAnalysisType.Logarithmic
            : DisplayAnalysisType.Linear,
      },
    };
  }

  // bind method with arrow syntax because it's expected to be called from within a Highcharts callback
  updateHoveredTimestampMs = (x: null | number) => {
    this.hoveredTimestampMs = x;
    if (this.annotations) {
      this.annotations.redrawCreateButton();
    }
  };

  // bind method with arrow syntax because it's expected to be used as a DOM event listener
  handleHighchartsContainerMouseMove = (ev: MouseEvent) => {
    // If hovering over x-axis labels (which is naturally the area below the minimum y-value), manually update the "create annotation" hover position
    const yAxis = this.highcharts.yAxis[0];
    const y = yAxis.toValue(ev.offsetY, false);
    if (y < yAxis.min) {
      const extendedMouseEvent = this.highcharts.pointer.normalize(ev);
      const nearestPoint = this.highcharts.pointer.findNearestKDPoint(
        this.highcharts.series,
        false,
        extendedMouseEvent,
      );
      if (nearestPoint && this.hoveredTimestampMs !== nearestPoint.x) {
        this.updateHoveredTimestampMs(nearestPoint.x);
      }
    }
  };

  // bind method with arrow syntax because it's expected to be used as a DOM event listener
  handleHighchartsContainerMouseLeave = () => {
    this.updateHoveredTimestampMs(null);
  };
}

export const LineChartEl = defineAndMakeJsxFactory(`mp-line-chart`, LineChart);
